<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { useField, useForm } from 'vee-validate'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import {
  getClosedPayrolls,
  getClosedProcesses,
  getAccountingDetails,
  updateDataHistory,
  sendExportProcessedPayroll,
  sendExportProcessedPayrollSAP,
  fetchLatestClosedProcesses,
  IClosedProcess,
  IDataExportProcessedPayroll
} from '@/app/core/services/processed-payrolls'
import { formatNumber } from '@/app/core/util/numbers'
import { useI18n } from 'vue-i18n'
import { object, string } from 'yup'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DLoading,
  DModal,
  SearchMdIcon,
  RefreshCw05Icon,
  ArrowRightIcon,
  DTextAreaField,
  InfoCircleIcon,
  Cube01Icon,
  Loading02Icon,
  TrendUp01Icon,
  ArrowCircleBrokenUpRightIcon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
usePayrollChange(() => window.location.reload())

dayjs.extend(utc)
dayjs.extend(timezone)
/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const payrollStore = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const showModalEntries = ref(false)
const showModalSendExport = ref(false)
const showDataAccounting = ref(false)
const showModalSendExportSAP = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')
const isRefreshData = ref<boolean>(false)

const loading = ref(false)
const messageLoading = ref('')
const listEntriesNetsuite = ref()

/* Datos */
const search = ref()
const tableClosedPayrolls = ref(null)
const urlFile = ref(null)
const refresh = ref()
const refreshUpdate = ref()
const payload = ref()
const processedForUpdate = ref()
const filterProcesses = ref()
const latestClosedProcesses = ref()
const dataProcessedPayroll = ref<any>({})
const validateIntegrationSAP = ref()

/* Datos Modal Accounting */
const listCreditDebit = ref<any[]>([])

/* Datos componente table */
const columns = ref([
  {
    name: 'process',
    title: t('processedPayrolls.list.process'),
    table: 'process'
  },
  {
    name: 'start_date',
    title: t('processedPayrolls.list.startDate'),
    table: 'start_date'
  },
  {
    name: 'end_date',
    title: t('processedPayrolls.list.endDate'),
    table: 'end_date'
  },
  { name: 'listEmployees', title: t('processedPayrolls.list.employees') },
  { name: 'listConcepts', title: t('processedPayrolls.list.concepts') },
  {
    name: 'list_entries',
    title: t('processedPayrolls.list.entriesNetsuite'),
    table: 'ledger_transactions'
  },
  {
    name: 'neto',
    title: t('processedPayrolls.list.net'),
    table: 'neto',
    format: (value: any) => formatNumber(value),
    align: 'end'
  },
  ...(payrollStore.payroll?.country?.iso_code === 'MX'
    ? [
        {
          name: 'stamped_payroll_id',
          title: t('processedPayrolls.list.stampedPayroll'),
          table: 'stamped_payroll_id',
          minWidth: '220px'
        }
      ]
    : []),
  {
    name: 'closed_by',
    title: t('processedPayrolls.list.closedBy'),
    table: 'closed_by'
  },
  {
    name: 'closed_date',
    title: t('processedPayrolls.list.closingDate'),
    table: 'closed_date'
  },
  { name: 'actions', title: '' }
])
const rows = ref<any[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

const formatDate = computed(() => {
  return (date: any) => dayjs.utc(date).format('DD/MM/YYYY')
})

const dataAccounting = ref<any[]>([])

/* Esquema de validacion campos obligatorios */
const validationSchema = object().shape({
  observation: string()
    .required(t('form.valid.required'))
    .min(20, t('form.valid.minLength', [20]))
    .max(300, t('form.valid.maxLength', [300]))
})

/* Uso del metodo de validación, retorno de errores */
const { handleSubmit, errors } = useForm<any>({})

/* Definicion de  campos del formulario */
const { value: observation } = useField<string>(ref('observation'))
const { value: processedPayrollId } = useField<string>(
  ref('processedPayrollId')
)

/// Para exportar a SAP
const { value: closeId } = useField<string>(ref('closeId'))
const { value: start_date } = useField<string>(ref('start_date'))
const { value: end_date } = useField<string>(ref('end_date'))
const { value: processCode } = useField<string>(ref('processCode'))

const onSubmitExport = handleSubmit(values => {
  void sendExportConfirm(values)
})

const onSubmitExportSAP = handleSubmit(values => {
  void sendExportConfirmSAP(values)
})

/* Escapa comillas simples para incrustar textos traducidos en el SQL */
const sqlText = (value: string) => `'${value.replaceAll("'", "''")}'`

/* Alias con el que llega un campo del query, con la misma regla del backend */
const fieldAlias = (field: string) => {
  const value = field.toLowerCase()
  if (value.includes(' as ')) {
    const parts = value.split(' ')
    return parts[parts.indexOf('as') + 1]
  }
  return value.includes('.') ? value.split('.')[1] : value
}

const composedReportFields = computed<Record<string, string>>(() => {
  const leftEmployees = 'jsonb_array_length(processed_payrolls.employees) - 1'
  const leftConcepts = 'jsonb_array_length(processed_payrolls.concepts) - 1'
  const entries = 'closed_payrolls.ledger_transactions::jsonb'

  return {
    listEmployees:
      `CONCAT(employees.first_name,' ',employees.lastname,` +
      ` CASE WHEN ${leftEmployees} > 0` +
      ` THEN CONCAT(' +', ${leftEmployees}) ELSE '' END) as first_employee`,
    listConcepts:
      `CONCAT(concepts.code,'-',concepts.name,` +
      ` CASE WHEN ${leftConcepts} > 0` +
      ` THEN CONCAT(' +', ${leftConcepts}) ELSE '' END) as first_concept`,
    list_entries:
      `CASE WHEN process_status.name = 'Pendiente'` +
      ` THEN ${sqlText(t('processedPayrolls.list.processingAccounting'))}` +
      ` WHEN closed_payrolls.ledger_transactions IS NULL THEN ''` +
      ` WHEN jsonb_typeof(${entries}) <> 'array' THEN ''` +
      ` WHEN jsonb_array_length(${entries}) = 0 THEN ''` +
      ` WHEN jsonb_array_length(${entries}) = 1` +
      ` AND ${entries} ->> 0 = 'Has errors'` +
      ` THEN ${sqlText(t('processedPayrolls.list.reviewErrors'))}` +
      ` WHEN jsonb_array_length(${entries}) = 1 THEN ${entries} ->> 0` +
      ` ELSE CONCAT(${sqlText(
        t('processedPayrolls.list.examineEntries')
      )}, ' (', jsonb_array_length(${entries}), ')')` +
      ` END as ledger_transactions`,
    stamped_payroll_id:
      `CASE WHEN stamped_payrolls.id IS NULL` +
      ` THEN ${sqlText(t('processedPayrolls.list.pendingStampedPayroll'))}` +
      ` ELSE ${sqlText(
        t('processedPayrolls.list.successStampedPayroll')
      )} END as stamped_payroll_id`
  }
})

const reportColumns = computed(() => {
  const queryFields: string[] = payload.value?.fields ?? []

  return columns.value
    .filter((column: any) => column.title !== '')
    .map((column: any) => {
      const field =
        composedReportFields.value[column.name] ??
        queryFields.find(
          (queryField: string) =>
            fieldAlias(queryField) === column.table?.toLowerCase()
        )
      if (!field) {
        console.warn(
          `[processedPayrolls] la columna "${column.name}" no se exporta: falta su campo en el query o su expresión en composedReportFields`
        )
        return null
      }
      return { field, header: column.title }
    })
    .filter(
      (column): column is { field: string; header: string } => column !== null
    )
})

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: reportColumns.value.map(column => column.field),
        headers: reportColumns.value.map(column => column.header),
        module: t('processedPayrolls.list.title')
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'processedPayrolls', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  showModalEntries.value = false
  showDataAccounting.value = false
  urlFile.value = null
}
const closeModalExport = () => {
  showModalSendExport.value = false
  showModalSendExportSAP.value = false
  observation.value = ''
}
/* */
const closedProcesses = async () => {
  isRefreshData.value = true
  try {
    const [closedProcessesRes, latestClosedProcessesRes, data] =
      await Promise.all([
        getClosedProcesses(),
        fetchLatestClosedProcesses(),
        getClosedPayrolls(
          offset.value,
          perPage.value,
          sortBy.value,
          search.value === '' ? undefined : search.value,
          payrollStore.payroll?.country?.iso_code
        )
      ])

    filterProcesses.value = closedProcessesRes[0].results
    latestClosedProcesses.value = latestClosedProcessesRes[0].results
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    validateIntegrationSAP.value =
      (data[0].results[0].integrations?.SAP_SERVICE_LAYER &&
        data[0].results[0]?.sap_allow_automatic_accounting == 'false') ||
      false
    payload.value = data[1]
    loading.value = false
    isRefreshData.value = false
  } catch (error: any) {
    isRefreshData.value = false
    loading.value = false
    notificationError(error, 'processedPayrolls', t, notifications, loading)
  }
}

const sheetPayroll = (id: string, closeId: string) => {
  const type: string = 'movements'
  void router.push({
    name: 'DetailPayrollSheet',
    params: { id, type, closeId }
  })
}

/** Enviar exportación APEX */
const sendExport = (data: IClosedProcess) => {
  showModalSendExport.value = true
  dataProcessedPayroll.value = data
  processedPayrollId.value = dataProcessedPayroll.value.processedId
}

const sendExportSAP = (data: IClosedProcess) => {
  console.log(data)
  showModalSendExportSAP.value = true
  observation.value = 'Envio de contabilización a SAP'
  dataProcessedPayroll.value = data
  processedPayrollId.value = dataProcessedPayroll.value.processedId
  closeId.value = dataProcessedPayroll.value.id
  start_date.value = dataProcessedPayroll.value.start_date
  end_date.value = dataProcessedPayroll.value.end_date
  processCode.value = dataProcessedPayroll.value.process_code
}

const sendExportConfirmSAP = async (values: IClosedProcess) => {
  loading.value = true
  showModalSendExportSAP.value = false
  messageLoading.value = t('loading.send')
  await sendExportProcessedPayrollSAP(
    closeId.value,
    dayjs.utc(values.start_date).format('YYYY-MM-DD'),
    dayjs.utc(values.end_date).format('YYYY-MM-DD'),
    processCode.value,
    payrollStore?.payroll?.id
  )
    .then(response => {
      notifications.addNotification({
        title: t('notification.sent'),
        text: response.message,
        type: response.code as any,
        time: 10000
      })
    })
    .catch((error: any) => {
      notificationError(error, 'processedPayrolls', t, notifications, loading)
    })
    .finally(() => {
      loading.value = false
    })
}

const sendExportConfirm = async (values: IDataExportProcessedPayroll) => {
  loading.value = true
  showModalSendExport.value = false
  messageLoading.value = t('loading.send')
  await sendExportProcessedPayroll(
    values.processedPayrollId,
    values.observation
  )
    .then(response => {
      notifications.addNotification({
        title: t('notification.sent'),
        text: response.message,
        type: response.code as any,
        time: 10000
      })
    })
    .catch((error: any) => {
      notificationError(error, 'processedPayrolls', t, notifications, loading)
    })
    .finally(() => {
      loading.value = false
      observation.value = ''
    })
}
/* Actualizar histórico */
const updateHistory = (id: string) => {
  typeAlertModal.value = 'question'
  titleModal.value = t('processedPayrolls.list.actionStatus')
  descriptionModal.value = t('modal.description')
  showModal.value = true
  processedForUpdate.value = id
}
const updateHistoryConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await updateDataHistory(processedForUpdate.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.updated'),
        text: t('processedPayrolls.notification.action'),
        type: 'success',
        time: 10000
      })
      void closedProcesses()
    })
    .catch((error: any) => {
      notificationError(error, 'processedPayrolls', t, notifications, loading)
    })
}

/* consultar detalle de asientos contables */
const getDetailsAccounting = async (data: IClosedProcess) => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const detailsAccounting = await getAccountingDetails(data.id)

    if (detailsAccounting[0]) {
      const detailsAccountingClear = detailsAccounting[0]

      if (detailsAccountingClear.status_code == 'COMPLETE') {
        dataAccounting.value = detailsAccounting[0].result_detail
        showDataAccounting.value = true
      } else if (detailsAccountingClear.status_code === 'ERROR') {
        typeAlertModal.value = 'error'
        titleModal.value = 'Error'
        if (detailsAccountingClear.errors.length === 1) {
          descriptionModal.value = t(
            'error.messages.' + detailsAccountingClear.errors[0].code,
            {
              field: validateTranslate(
                'fields.' + detailsAccountingClear.errors[0].params.field
              ),
              entity: t(
                'fields.' + detailsAccountingClear.errors[0].params.entity
              ),
              record: detailsAccountingClear.errors[0].params.record,
              value: detailsAccountingClear.errors[0].params.value,
              payroll:
                detailsAccountingClear.errors[0].params.payrollCode &&
                detailsAccountingClear.errors[0].params.payrollCode != ''
                  ? t('fields.payroll') +
                    ' ' +
                    detailsAccountingClear.errors[0].params.payrollCode
                  : '',
              message: detailsAccountingClear.errors[0].params.message
            }
          )
        } else {
          descriptionModal.value = '<div class="text-left">'
          for (let i = 0; i < detailsAccountingClear.errors.length; i++) {
            const error = t(
              'error.messages.' + detailsAccountingClear.errors[i].code,
              {
                field: validateTranslate(
                  'fields.' + detailsAccountingClear.errors[i].params.field
                ),
                entity: t(
                  'fields.' + detailsAccountingClear.errors[i].params.entity
                ),
                record: detailsAccountingClear.errors[i].params.record,
                payroll:
                  detailsAccountingClear.errors[i].params.payrollCode &&
                  detailsAccountingClear.errors[i].params.payrollCode != ''
                    ? t('fields.payroll') +
                      ' ' +
                      detailsAccountingClear.errors[i].params.payrollCode
                    : ''
              }
            )
            descriptionModal.value += '- ' + error
            if (i < detailsAccountingClear.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
          descriptionModal.value += '</div>'
        }
        showModal.value = true
        loading.value = false
      }
    }

    loading.value = false
  } catch (error: any) {
    notificationError(error, 'processedPayrolls', t, notifications, loading)
  }
}

const openSingleLedgerTransaction = (data: any) => {
  void getDetailsAccounting(data)
  listEntriesNetsuite.value = data.ledger_transactions
}

const openLedgerTransactions = (data: any) => {
  void getDetailsAccounting(data)
  showModalEntries.value = true
  listEntriesNetsuite.value = data.ledger_transactions
}

function validateTranslate(code: string): any {
  const translated = t(code)
  if (translated === code) {
    return code
  }
  return translated
}

/* animación para mostrar/ocultar detalles */
const dataDetails = (id: number, lines: number, show: boolean) => {
  console.log(id, lines, show)
  const height = 1 + lines
  if (show) {
    document
      .getElementById('details' + id)
      ?.setAttribute(
        'style',
        'overflow-y: auto; max-height: ' +
          lines * 35 +
          'px; transition: max-height;' +
          height / (6 * 1) +
          's ease-out'
      )
    document
      .getElementById('btn-show' + id)
      ?.setAttribute('style', 'display: none')
    document
      .getElementById('btn-hide' + id)
      ?.setAttribute('style', 'display: block')
    setTimeout(() => {}, height * (80 / 1) + 40)
  } else {
    document
      .getElementById('details' + id)
      ?.setAttribute(
        'style',
        'max-height: 0px; transition: max-height ' +
          height / (6 * 1) +
          's ease-out'
      )
    document
      .getElementById('btn-hide' + id)
      ?.setAttribute('style', 'display: none')
    document
      .getElementById('btn-show' + id)
      ?.setAttribute('style', 'display: block')
  }
}

onMounted(() => {
  console.log('Mounted List Closed Payrolls')
  refreshUpdate.value = setInterval(() => {
    void closedProcesses()
  }, 10000)
})

onUnmounted(() => {
  clearInterval(refreshUpdate.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showDataAccounting"
    :title="t('processedPayrolls.list.entriesNetsuite')"
    :size="'w-2/4 md:w-4/5 lg:w-2/3 xl:w-3/4 2xl:w-2/4'"
    @closeModal="closeModal">
    <template
      v-if="dataAccounting && dataAccounting.length > 0"
      v-slot:description>
      <div>
        <div
          v-for="(item, x) in dataAccounting"
          class="flex flex-col gap-4 overflow-hidden"
          :class="[x + 1 === dataAccounting.length ? '' : 'mb-5']">
          <div
            class="flex flex-col justify-between border-2 sm:w-full rounded-lg bg-white h-auto min-h-0"
            style="margin-bottom: -5px">
            <div>
              <div class="grid grid-cols-3 p-4">
                <div class="col-span-1">
                  <p class="mb-3 text-end font-semibold">
                    {{ dayjs(item.startDate).utc().format('DD/MM/YYYY') }} -
                    {{ dayjs(item.endDate).utc().format('DD/MM/YYYY') }}
                  </p>
                  <div class="grid grid-cols-2 text-center">
                    <div class="col-span-1 border-r border-r-gray-300">
                      <p
                        class="text-gray-600 border-b border-b-gray-300 pb-1"
                        v-html="t('reports.accountingEntry.generate.debit')" />
                      <p
                        class="font-semibold rounded-full text-sm inline-flex px-2 py-1 mt-2"
                        :class="[
                          item.totalDebit === item.totalCredit
                            ? 'text-green-700 bg-green-100'
                            : 'text-red-700 bg-red-100'
                        ]">
                        {{ formatNumber(item.totalDebit) }}
                      </p>
                    </div>
                    <div class="col-span-1">
                      <p
                        class="text-gray-600 border-b border-b-gray-300 pb-1"
                        v-html="t('reports.accountingEntry.generate.credit')" />
                      <p
                        class="font-semibold rounded-full text-sm inline-flex px-2 py-1 mt-2"
                        :class="[
                          item.totalDebit === item.totalCredit
                            ? 'text-green-700 bg-green-100'
                            : 'text-red-700 bg-red-100'
                        ]">
                        {{ formatNumber(item.totalCredit) }}
                      </p>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 mt-5">
                    <div class="col-span-1 text-right">
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.allocationsType')
                        " />
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.deductionsType')
                        " />
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.netPayroll')
                        " />
                    </div>
                    <div class="col-span-1 text-center">
                      <p class="font-semibold text-gray-600">
                        {{ formatNumber(item.totalAssignments) }}
                      </p>
                      <p class="font-semibold text-gray-600">
                        {{ formatNumber(item.totalDeductions) }}
                      </p>
                      <p class="font-semibold text-gray-600">
                        {{ formatNumber(item.totalPayroll) }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="col-span-2">
                  <p class="mb-3 font-semibold">
                    {{ item.payroll.name }} - #{{ listEntriesNetsuite[x] }}
                  </p>
                </div>
              </div>
              <hr class="mx-4" />
              <div
                class="overflow-hidden max-h-0"
                :id="'details' + x"
                style="max-height: 0">
                <div>
                  <div class="p-4">
                    <div class="grid grid-cols-4 bg-gray-100 px-3 py-1 mb-1">
                      <div class="sm:col-span-2 md:col-span-1 text-left">
                        <p
                          class="font-semibold text-gray-700"
                          v-html="
                            t('reports.accountingEntry.generate.entryNumber', [
                              listEntriesNetsuite[x]
                            ])
                          " />
                      </div>
                      <div class="sm:col-span-2 md:col-span-1 text-left">
                        <p class="text-gray-600">
                          {{
                            t(
                              'reports.accountingEntry.generate.allocationsEntryType'
                            )
                          }}
                          <span class="font-semibold text-gray-700">
                            {{ formatNumber(item.totalAssignments) }}
                          </span>
                        </p>
                      </div>
                      <div class="sm:col-span-2 md:col-span-1 text-right">
                        <p class="text-gray-600">
                          {{
                            t(
                              'reports.accountingEntry.generate.deductionsEntryType'
                            )
                          }}
                          <span class="font-semibold text-gray-700">
                            {{ formatNumber(item.totalDeductions) }}
                          </span>
                        </p>
                      </div>
                      <div class="sm:col-span-2 md:col-span-1 text-right">
                        <p class="text-gray-600">
                          {{
                            t(
                              'reports.accountingEntry.generate.netPayrollEntryType'
                            )
                          }}
                          <span class="font-semibold text-gray-700">
                            {{ formatNumber(item.totalPayroll) }}
                          </span>
                        </p>
                      </div>
                    </div>
                    <table class="w-full text-left">
                      <thead
                        class="text-gray-500 bg-gray-100 font-semibold border-b border-b-gray-200">
                        <tr>
                          <th class="px-5">
                            {{
                              t(
                                'reports.accountingEntry.generate.accountingAccount'
                              )
                            }}
                          </th>
                          <th class="px-5">
                            {{
                              t('reports.accountingEntry.generate.costCenter')
                            }}
                          </th>
                          <th class="px-5">
                            {{ t('reports.accountingEntry.generate.concepts') }}
                          </th>
                          <th class="px-5">
                            {{
                              t('reports.accountingEntry.generate.debitAmount')
                            }}
                          </th>
                          <th class="px-5">
                            {{
                              t('reports.accountingEntry.generate.creditAmount')
                            }}
                          </th>
                        </tr>
                      </thead>
                      <tbody class="text-gray-600">
                        <tr
                          v-for="line in item.lines"
                          class="border-b border-b-gray-200">
                          <td class="px-5">
                            {{
                              `${line?.account?.code || ''} - ${
                                line?.account?.name || ''
                              }`
                            }}
                          </td>
                          <td class="px-5">
                            {{ line?.costCenter?.code || '' }}
                          </td>
                          <td class="px-5">
                            <Popper
                              arrow
                              offsetDistance="14"
                              :hover="false"
                              placement="right"
                              class="tooltip">
                              <div class="cursor-pointer">
                                <Cube01Icon
                                  class="w-5 h-5"
                                  style="
                                    margin-top: 3px;
                                    margin-bottom: -3px;
                                  " />
                              </div>
                              <template #content>
                                <div class="text-left">
                                  <div v-for="concept in line.concepts">
                                    • {{ concept }}
                                  </div>
                                </div>
                              </template>
                            </Popper>
                          </td>
                          <td class="px-5 text-gray-700 font-semibold">
                            {{
                              line.type === 'D'
                                ? formatNumber(line.amount)
                                : formatNumber(0)
                            }}
                          </td>
                          <td class="px-5 text-gray-700 font-semibold">
                            {{
                              line.type === 'C'
                                ? formatNumber(line.amount)
                                : formatNumber(0)
                            }}
                          </td>
                        </tr>
                      </tbody>
                      <tfoot class="text-gray-600">
                        <tr class="border-b border-b-gray-200">
                          <td colspan="3" class="text-right px-5">
                            {{
                              t('reports.accountingEntry.generate.entryType')
                            }}
                          </td>
                          <td class="px-5 text-gray-700 font-semibold">
                            {{ formatNumber(item.totalDebit) }}
                          </td>
                          <td class="px-5 text-gray-700 font-semibold">
                            {{ formatNumber(item.totalCredit) }}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
              <div class="flex justify-center hidden" :id="'btn-hide' + x">
                <p
                  class="cursor-pointer text-center font-bold text-blue-600 p-2"
                  @click="dataDetails(x, item.lines.length, false)"
                  v-html="t('reports.accountingEntry.generate.hideDetails')" />
              </div>
              <div class="flex justify-center" :id="'btn-show' + x">
                <p
                  class="cursor-pointer text-center font-bold text-blue-600 p-2"
                  @click="dataDetails(x, item.lines.length, true)"
                  v-html="t('reports.accountingEntry.generate.showDetails')" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </DModal>
  <DModal
    :open="showModalSendExportSAP"
    :isFooter="false"
    :size="'w-1/4 md:w-2/5 lg:w-1/3 xl:w-1/4 2xl:w-1/4'"
    @closeModal="closeModalExport">
    <template v-if="dataProcessedPayroll" v-slot:form>
      <div class="flex flex-col items-center gap-10">
        <section
          class="flex items-center justify-center rounded-full sm:mx-0 h-14 w-14 bg-red-100">
          <InfoCircleIcon class="h-8 w-8 text-yellow-500" />
        </section>
        <section class="px-5 text-center text-gray-400 text-sm">
          <h1
            class="text-black text-xl"
            v-html="t('processedPayrolls.list.exports')" />
          <p class="mt-3">
            {{ t('processedPayrolls.export.confirmation') }}
            <span class="text-gray-500 font-bold">{{
              dataProcessedPayroll.process
            }}</span>
            {{ t('processedPayrolls.export.datePrompt') }}
            <span class="text-gray-500 font-bold">
              {{ formatDate(dataProcessedPayroll.start_date) }} -
              {{ formatDate(dataProcessedPayroll.end_date) }}
            </span>
          </p>
          <p
            class="mt-5"
            v-html="t('processedPayrolls.export.confirmationSend')" />
        </section>
        <section class="flex gap-3">
          <button
            @click="closeModalExport"
            class="hover:bg-gray-600 hover:text-white focus:ring-gray-200 inline-flex text-sm justify-center items-center py-1.5 px-10 border border-gray-300 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
            v-html="t('form.button.cancel')" />
          <button
            @click="onSubmitExportSAP"
            class="bg-green-100 text-green-800 hover:bg-green-700 hover:text-white focus:ring-red-200 inline-flex text-sm justify-center items-center py-1.5 px-10 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
            v-html="t('processedPayrolls.export.button')" />
        </section>
      </div>
    </template>
  </DModal>
  <DModal
    :open="showModalSendExport"
    :isFooter="false"
    :size="'w-1/4 md:w-2/5 lg:w-1/3 xl:w-1/4 2xl:w-1/4'"
    @closeModal="closeModalExport">
    <template v-if="dataProcessedPayroll" v-slot:form>
      <div class="flex flex-col items-center gap-10">
        <section
          class="flex items-center justify-center rounded-full sm:mx-0 h-14 w-14 bg-red-100">
          <InfoCircleIcon class="h-8 w-8 text-yellow-500" />
        </section>
        <section class="px-5 text-center text-gray-400 text-sm">
          <h1
            class="text-black text-xl"
            v-html="t('processedPayrolls.list.exports')" />
          <p class="mt-3">
            {{ t('processedPayrolls.export.confirmation') }}
            <span class="text-gray-500 font-bold">{{
              dataProcessedPayroll.process
            }}</span>
            {{ t('processedPayrolls.export.datePrompt') }}
            <span class="text-gray-500 font-bold">
              {{ formatDate(dataProcessedPayroll.start_date) }} -
              {{ formatDate(dataProcessedPayroll.end_date) }}
            </span>
            {{ t('processedPayrolls.export.deleteWarning') }}
          </p>
          <p
            class="mt-5"
            v-html="t('processedPayrolls.export.confirmationMessage')" />
          <DTextAreaField
            v-model="observation"
            :placeholder="t('processedPayrolls.export.reason')"
            :error="errors.observation"
            :rows="7" />
        </section>
        <section class="flex gap-3">
          <button
            @click="closeModalExport"
            class="hover:bg-gray-600 hover:text-white focus:ring-gray-200 inline-flex text-sm justify-center items-center py-1.5 px-10 border border-gray-300 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
            v-html="t('form.button.cancel')" />
          <button
            @click="onSubmitExport"
            class="bg-green-100 text-green-800 hover:bg-green-700 hover:text-white focus:ring-red-200 inline-flex text-sm justify-center items-center py-1.5 px-10 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
            v-html="t('processedPayrolls.export.button')" />
        </section>
      </div>
    </template>
  </DModal>
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    @closeModal="closeModal"
    @otherMethod="updateHistoryConfirm()">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <DModal
    :open="showModalEntries"
    :title="t('processedPayrolls.list.errorEntries')"
    @closeModal="closeModal">
    <template v-slot:description>
      <div class="text-left" style="max-height: calc(100vh - 15rem)">
        <div
          v-for="(error, index) in listEntriesNetsuite"
          :key="index"
          class="text-gray-600">
          <span class="text-blue-700">&#9205;</span> {{ error }}
        </div>
      </div>
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('processedPayrolls.list.title')" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('processedPayrolls.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="closedProcesses" />
          <DBtn
            @click.prevent="closedProcesses"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex justify-end gap-2">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
        </div>
      </div>
    </div>
    <DTable
      ref="tableClosedPayrolls"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), closedProcesses()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), closedProcesses()">
      <template #listEmployees="data: any">
        <div class="flex items-center w-[200px] justify-between gap-x-1">
          <div>
            {{ data.first_employee }}
          </div>
          <div
            v-if="data.left_employees > 0"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            +{{ data.left_employees }}
          </div>
        </div>
      </template>
      <template #listConcepts="data: any">
        <div class="flex items-center w-[200px] justify-between gap-x-1">
          <div>
            {{ data.first_concept }}
          </div>
          <div
            v-if="data.left_concepts > 0"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            +{{ data.left_concepts }}
          </div>
        </div>
      </template>
      <template #list_entries="data: any">
        <div
          class="flex flex-col items-center"
          v-if="data.status_accounting === 'Pendiente'">
          <Loading02Icon class="rotate text-blue-600" />
          <span class="font-black text-xs tracking-wider text-blue-500">
            {{ t('processedPayrolls.list.processingAccounting') }}</span
          >
        </div>
        <div v-else class="my-1.5">
          <div
            v-if="
              Array.isArray(data.ledger_transactions) &&
              data.ledger_transactions.length === 1 &&
              data.ledger_transactions[0] === 'Has errors'
            "
            class="bg-red-50 rounded-lg text-red-700 text-sm font-semibold py-1 px-2 mt-1.5 w-max cursor-pointer"
            @click="getDetailsAccounting(data)">
            <p>
              {{ t('processedPayrolls.list.reviewErrors') }}
            </p>
          </div>
          <div
            v-if="
              Array.isArray(data.ledger_transactions) &&
              data.ledger_transactions.length === 1 &&
              data.ledger_transactions[0] != 'Has errors'
            "
            @click="openSingleLedgerTransaction(data)">
            <p class="text-sm text-gray-500">
              <span class="text-blue-700">&#9205;</span>
              {{ data.ledger_transactions[0] }}
            </p>
          </div>
          <div
            v-if="
              Array.isArray(data.ledger_transactions) &&
              data.ledger_transactions.length > 1
            "
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold py-1 px-2 mt-1.5 w-max cursor-pointer"
            @click="openLedgerTransactions(data)">
            <p>
              {{ t('processedPayrolls.list.examineEntries') }}
              ({{ data.ledger_transactions.length }})
            </p>
          </div>
        </div>
      </template>
      <template #stamped_payroll_id="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.stamped_payroll_id
              ? 'bg-green-100 text-green-600'
              : 'bg-orange-100 text-orange-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="
              data.stamped_payroll_id ? 'bg-green-600' : 'bg-orange-600'
            "></div>
          {{
            data.stamped_payroll_id
              ? t('processedPayrolls.list.successStampedPayroll')
              : t('processedPayrolls.list.pendingStampedPayroll')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="
              store.hasPermission('payroll_closed:create') &&
              data.process != 'LIQUIDACION' && filterProcesses.find((obj: any) => obj.id === data.id)
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.updateHistory')"
            :hover="true"
            placement="left"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateHistory(data.processedId)">
              <RefreshCw05Icon
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              validateIntegrationSAP &&
              data.ledger_transactions &&
              Array.isArray(data.ledger_transactions) &&
              data.ledger_transactions.length > 0 &&
              data.ledger_transactions[0] != 'Has errors'
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.hasAccountingSent')"
            :hover="true"
            placement="left"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2">
              <ArrowCircleBrokenUpRightIcon
                class="h-6 w-6 mx-2 cursor-pointer text-gray-400 cursor-not-allowed opacity-60" />
            </div>
          </Popper>
          <Popper
            v-if="
              validateIntegrationSAP &&
              (!data.ledger_transactions ||
                (Array.isArray(data.ledger_transactions) &&
                  (data.ledger_transactions.length == 0 ||
                    data.ledger_transactions[0] == 'Has errors')))
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.exportPayrollSAP')"
            :hover="true"
            placement="left"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="sendExportSAP(data)">
              <ArrowCircleBrokenUpRightIcon
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              store.hasPermission('apex:execute') &&
              latestClosedProcesses.find((obj: any) => obj.id === data.id)
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.exportPayroll')"
            :hover="true"
            placement="left"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="sendExport(data)">
              <ArrowRightIcon
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('payroll_closed:create')"
            arrow
            offsetDistance="12"
            :content="t('table.action.visualize')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="sheetPayroll(data.processedId, data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
      <template #refresh v-if="isRefreshData">
        <Loading02Icon class="rotate text-gray-400 mr-2" />
        <h1 class="text-gray-400 text-base" v-html="t('import.list.loading')" />
      </template>
    </DTable>
  </div>
</template>
